// client/src/pages/CreateNotification.jsx
import React, { useState, useEffect, useContext } from 'react';
import DateTimePicker from 'react-datetime-picker';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { OnboardingContext } from '../contexts/OnboardingContext';
import OnboardGuard from '../components/OnboardGuard';
import { Bell, Calendar, Clock, Edit2, Trash2, CheckCircle, ArrowRight } from 'lucide-react';

/** Local UI components */
const Logo = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'h-12',
    md: 'h-20',
    lg: 'h-64',
    xl: 'max-h-[420px]',
  };
  return (
    <img
      src="/STB_LOGO_V1.png"
      alt="SecureTheBag Logo"
      className={`${sizes[size] || sizes.md} w-auto object-contain ${className}`}
    />
  );
};

const Footer = () => (
  <footer className="mt-auto py-4 text-center text-sm text-gray-500">
    © {new Date().getFullYear()} SecureTheBag
  </footer>
);


const WEEK_DAYS = [
  'Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'
];

// Normalize Postgres int[] (“{1,3,5}”), arrays of strings, etc. → number[]
function toNumArray(val) {
  if (!val && val !== 0) return [];
  if (Array.isArray(val)) {
    return val
      .map(v => (typeof v === 'number' ? v : Number(v)))
      .filter(v => Number.isFinite(v));
  }
  if (typeof val === 'string') {
    // Strip PG braces if present and split
    const inner = val.startsWith('{') && val.endsWith('}') ? val.slice(1, -1) : val;
    if (!inner.trim()) return [];
    return inner
      .split(',')
      .map(s => Number(s.trim()))
      .filter(v => Number.isFinite(v));
  }
  return [];
}

// Normalize API datetime strings to a Date. We treat values without an offset as UTC.
function toDateFromApi(raw) {
  if (!raw) return null;
  let s = String(raw).trim();
  // If it already has a timezone offset or Z, parse directly
  if (/Z$|[+-]\d{2}:\d{2}$/.test(s)) return new Date(s);
  // Convert "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ssZ" (UTC)
  s = s.replace(' ', 'T');
  if (!/Z$|[+-]\d{2}:\d{2}$/.test(s)) s += 'Z';
  return new Date(s);
}

function CreateNotificationForm() {
  const navigate = useNavigate();
  const { setOnboardingStepIfNotComplete } = useContext(OnboardingContext);

  // State for existing notifications
  const [notifications, setNotifications] = useState([]);

  // State for plan limit
  const [maxNotifications, setMaxNotifications] = useState(1);
  // State for user's timezone
  const [timezone, setTimezone] = useState('');

  useEffect(() => {
    async function loadInitialData() {
      try {

        console.log('[CreateNotification] Loading initial data... /notifications');

        // 1) load existing notifications
        const notifRes = await api.get('/api/notifications');
        // Backend returns paginated response: { items: [...], pagination: {...} }
        setNotifications(notifRes.data.items || []);

        console.log('[CreateNotification] Loading initial data... /subscriptions/current');

        // 2A) fetch current subscription
        const subRes = await api.get('/api/subscriptions/current');
        const { priceId } = subRes.data;

        console.log('[CreateNotification] Loading initial data... /subscriptions/plans');

        // 2B) fetch all plan definitions
        const { data: { plans } } = await api.get('/api/subscriptions/plans');

        console.log('[CreateNotification] Loading initial data... AND???');

        // 2C) find the matching plan object
        const plan = plans.find(p => p.id === priceId);

        // print plan for debugging
        console.log('Current plan:', plan);
        console.log('Plan metadata:', plan?.metadata);

        // 2D) Determine notification limit from plan metadata or features
        let limit = 1;
        if (plan) {
          // First, try metadata.notifications_per_day
          const meta = plan.metadata || {};
          if (meta.notifications_per_day) {
            const n = parseInt(meta.notifications_per_day, 10);
            if (!Number.isNaN(n) && n > 0) {
              limit = n;
            }
          }
          // Fallback: parse features array for "X SMS per day"
          else if (Array.isArray(plan.features) && plan.features.length > 0) {
            const hit = plan.features.find(f => /\b(1|2|3)\s*SMS\s*per\s*day\b/i.test(f));
            if (hit) {
              const m = hit.match(/\b(1|2|3)\b/);
              if (m) limit = parseInt(m[1], 10);
            }
          }
          // Fallback: check nickname
          else if (typeof plan.nickname === 'string') {
            if (/three|3/i.test(plan.nickname)) limit = 3;
            else if (/two|2/i.test(plan.nickname)) limit = 2;
            else if (/basic|one|1/i.test(plan.nickname)) limit = 1;
          }
        }

        setMaxNotifications(limit);
        console.log('Max notifications limit set to:', limit);

      } catch (err) {
        console.error('Error loading subscriptions/plans', err);
        setMaxNotifications(1);
      }
    }
    loadInitialData();
  }, [navigate]);

  // Form state
  const [title, setTitle] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [phones, setPhones] = useState([]);
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedPhone, setSelectedPhone] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [error, setError] = useState('');

  // Track which notification is being edited (null = create mode)
  const [editingId, setEditingId] = useState(null);

  // Schedule fields
  const [onceDateTime, setOnceDateTime] = useState(null);
  const [dailyTime, setDailyTime] = useState('12:00');
  const [weeklyTime, setWeeklyTime] = useState('12:00');
  const [monthlyTime, setMonthlyTime] = useState('12:00');
  // For weekly multi-day picker
  const [weeklyDays, setWeeklyDays] = useState([]);
  // For monthly multi-day picker
  const [monthlyDays, setMonthlyDays] = useState([]);
  // Toggle a day in weeklyDays
  const toggleWeeklyDay = (dayIndex) => {
    setWeeklyDays(prev =>
      prev.includes(dayIndex)
        ? prev.filter(d => d !== dayIndex)
        : [...prev, dayIndex]
    );
  };
  // Toggle a day in monthlyDays
  const toggleMonthlyDay = (dayNumber) => {
    setMonthlyDays(prev =>
      prev.includes(dayNumber)
        ? prev.filter(d => d !== dayNumber)
        : [...prev, dayNumber]
    );
  };

  // Clear the form and exit edit mode
  const clearForm = () => {
    setEditingId(null);
    setTitle('');
    setSelectedBank('');
    setSelectedPhone('');
    setFrequency('daily');
    setDailyTime('12:00');
    setWeeklyTime('12:00');
    setMonthlyTime('12:00');
    setWeeklyDays([]);
    setMonthlyDays([]);
    setError('');
  };

  // Populate form with notification data for editing
  const startEditing = (notification) => {
    setEditingId(notification.id);
    setTitle(notification.title);
    setSelectedBank(notification.bank_account_id);
    setSelectedPhone(notification.phone_id);
    setFrequency(notification.frequency || 'daily');

    // Extract time from start_datetime
    const rawDate = notification.start_datetime || notification.scheduled_time;
    const date = toDateFromApi(rawDate);

    if (date && !isNaN(date.getTime())) {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const timeString = `${hours}:${minutes}`;

      switch (notification.frequency) {
        case 'daily':
          setDailyTime(timeString);
          break;
        case 'weekly':
          setWeeklyTime(timeString);
          setWeeklyDays(toNumArray(notification.weekdays));
          break;
        case 'monthly':
          setMonthlyTime(timeString);
          setMonthlyDays(toNumArray(notification.monthdays));
          break;
        default:
          setDailyTime(timeString);
      }
    }

    // Scroll to form
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  // disable form when user has reached their plan limit (but allow editing existing)
  const reachedLimit = !editingId && notifications.length >= maxNotifications;

  // Load bank accounts and phones
  useEffect(() => {
    api
      .get('/api/plaid/accounts')
      .then(res => setBankAccounts(res.data.accounts))
      .catch(console.error);

    api
      .get('/api/sms/phones')
      .then(res => setPhones(res.data.phones))
      .catch(console.error);
    // Load user profile for timezone
    api
      .get('/api/user/me')
      .then(res => setTimezone(res.data.user.timezone))
      .catch(console.error);
  }, []);


  // Render the schedule inputs per frequency
  const renderScheduleSection = (disabled) => {
    switch (frequency) {
      case 'daily':
        return (
          <input
            type="time"
            value={dailyTime}
            onChange={e => setDailyTime(e.target.value)}
            className="px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={disabled}
          />
        );
      case 'weekly':
        return (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {WEEK_DAYS.map((d, idx) => (
                <label key={d} className="flex items-center gap-1.5 text-sm cursor-pointer min-h-[44px] min-w-[44px] px-1">
                  <input
                    type="checkbox"
                    checked={weeklyDays.includes(idx + 1)}
                    onChange={() => toggleWeeklyDay(idx + 1)}
                    disabled={disabled}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                  />
                  <span>{d.slice(0,3)}</span>
                </label>
              ))}
            </div>
            <input
              type="time"
              value={weeklyTime}
              onChange={e => setWeeklyTime(e.target.value)}
              className="px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={disabled}
            />
          </div>
        );
      case 'monthly':
        return (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                <label key={day} className="flex items-center gap-1.5 text-sm cursor-pointer min-h-[44px] min-w-[44px] px-1">
                  <input
                    type="checkbox"
                    checked={monthlyDays.includes(day)}
                    onChange={() => toggleMonthlyDay(day)}
                    disabled={disabled}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                  />
                  <span>{day}</span>
                </label>
              ))}
            </div>
            <input
              type="time"
              value={monthlyTime}
              onChange={e => setMonthlyTime(e.target.value)}
              className="px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={disabled}
            />
          </div>
        );
      default:
        return null;
    }
  };

  // Create or update notification
  const saveNotification = async () => {
    setError('');
    if (!title.trim()) return setError('Title is required.');
    if (!selectedBank) return setError('Please choose a bank account.');
    if (!selectedPhone) return setError('Please choose a phone number.');

    // assemble scheduleTimes
    let scheduleTimes = [];
    const now = new Date();
    switch (frequency) {
      case 'daily': {
        const [h, m] = dailyTime.split(':').map(Number);
        const dt = new Date(now); dt.setHours(h, m, 0, 0);
        scheduleTimes.push(dt.toISOString());
        break;
      }
      case 'weekly': {
        const [h, m] = weeklyTime.split(':').map(Number);
        // For each selected weekday (1=Monday,...,7=Sunday)
        (weeklyDays.length > 0 ? weeklyDays : [1]).forEach(dayNum => {
          // JS Date.getDay(): 0=Sunday, 1=Monday,...,6=Saturday
          // dayNum: 1=Monday,...,7=Sunday
          const jsDay = (dayNum % 7); // 1=>1, ..., 7=>0 (Sunday)
          const currDay = now.getDay();
          let diff = jsDay - currDay;
          if (diff <= 0) diff += 7;
          const dt = new Date(now); dt.setDate(now.getDate() + diff); dt.setHours(h, m, 0, 0);
          scheduleTimes.push(dt.toISOString());
        });
        break;
      }
      case 'monthly': {
        const [h, m] = monthlyTime.split(':').map(Number);
        // For each selected day (1-31)
        (monthlyDays.length > 0 ? monthlyDays : [1]).forEach(dayNum => {
          const dt = new Date(now); dt.setDate(dayNum); dt.setHours(h, m, 0, 0);
          scheduleTimes.push(dt.toISOString());
        });
        break;
      }
    }

    try {
      // Build recurrence payload
      const payload = {
        title,
        phoneId: selectedPhone,
        bankAccountId: selectedBank,
        // For daily: send timeOfDay (HH:mm) instead of startDateTime to avoid timezone issues
        timeOfDay: frequency === 'daily' ? dailyTime : undefined,
        // For weekly/monthly: send startDateTime
        startDateTime: frequency !== 'daily' ? scheduleTimes[0] : undefined,
        frequency,
        interval: 1, // default every-1 period
        weekdays:
          frequency === 'weekly'
            ? (weeklyDays.length > 0 ? weeklyDays.slice() : [1])
            : null,
        monthdays:
          frequency === 'monthly'
            ? (monthlyDays.length > 0 ? monthlyDays.slice() : [1])
            : null,
        months: null,
        set_positions: null,
        tzMode: 'floating', // Use floating timezone (user's local time)
        timezone: null, // null for floating mode
      };

      if (editingId) {
        // UPDATE existing notification with full recurrence support
        const res = await api.put(`/api/notifications/${editingId}`, payload);
        if (res.data.success && res.data.notification) {
          // Update in the list
          setNotifications(prev =>
            prev.map(n => n.id === editingId ? res.data.notification : n)
          );
          clearForm();
        } else {
          setError('Failed to update notification.');
        }
      } else {
        // CREATE new notification
        console.log('[CreateNotification] Sending payload:', payload);
        const res = await api.post('/api/notifications/create', payload);
        if (res.data.success && res.data.notification) {
          // Prepend the newly created notification
          setNotifications(prev => [res.data.notification, ...prev]);
          clearForm();
        } else {
          setError('Failed to create notification.');
        }
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Server error.');
    }
  };

  // Delete a notification
  const deleteNotification = async (id) => {
    await api.delete(`/api/notifications/${id}`);
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Main grid */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-2">
        {/* Left visual (lg+) */}
        <div
          className="hidden md:flex m-6 rounded-3xl items-center justify-center p-12"
          style={{ backgroundColor: '#2b5f78' }}
        >
          <Logo size="xl" className="drop-shadow-xl" />
        </div>

        {/* Right: content */}
        <div className="flex items-start justify-center p-6 sm:p-8 md:p-12 lg:p-16 overflow-y-auto">
          <div className="w-full max-w-2xl">
            {/* Mobile logo only */}
            <div className="mb-6 sm:mb-8 flex justify-center md:hidden">
              <Logo size="md" />
            </div>

            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-orange-100 p-3 rounded-full">
                  <Bell className="w-6 h-6 text-orange-600" />
                </div>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 text-center">
                Create SMS Notifications
              </h1>
              <p className="text-gray-600 text-center mb-4">
                Set up balance alerts for your accounts
              </p>

              {/* Progress indicator */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 text-center">
                <div className="text-sm text-gray-600">
                  Created <span className="font-bold text-gray-900">{notifications.length}</span> of{' '}
                  <span className="font-bold text-gray-900">{maxNotifications}</span> notifications
                </div>
                {timezone && (
                  <div className="text-sm text-gray-500 mt-1">
                    Your timezone: <span className="font-medium">{timezone}</span>
                  </div>
                )}
              </div>
            </div>

            {/* List existing notifications */}
            {notifications.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Notifications</h3>
                <div className="space-y-3">
                  {notifications.map(n => (
                    <div
                      key={n.id}
                      className={`rounded-lg border p-4 ${
                        editingId === n.id
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">{n.title}</h4>
                          <div className="text-sm text-gray-600">
                            {(() => {
  const raw = n.start_datetime || n.scheduled_time;
  const effectiveTz = (n.tz_mode === 'fixed' && n.tz_id) ? n.tz_id : timezone;

  // Treat naive strings as UTC, then format in the effective tz.
  const d = toDateFromApi(raw);
  const timeStr = d
    ? d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: effectiveTz || undefined,
      })
    : '—';

  switch (n.frequency) {
    case 'daily':
      return `Daily at ${timeStr}.`;

    case 'weekly': {
      const src = (() => {
        const raw = n.weekdays ?? (n.rule ? n.rule.weekdays : undefined);
        return toNumArray(raw);
      })();

      const labels = src.length
        ? src.map((val) => {
            // Support 1..7 (Mon..Sun) or 0..6 (Sun..Sat)
            if (Number.isInteger(val)) {
              if (val >= 1 && val <= 7) {
                return WEEK_DAYS[val - 1].slice(0, 3);
              }
              if (val >= 0 && val <= 6) {
                const idx = (val + 6) % 7;
                return WEEK_DAYS[idx].slice(0, 3);
              }
            }
            return '?';
          }).join(', ')
        : '(no days set)';

      return `Weekly on ${labels} at ${timeStr}.`;
    }

    case 'monthly': {
      const src = (() => {
        const raw = n.monthdays ?? (n.rule ? n.rule.monthdays : undefined);
        return toNumArray(raw);
      })();
      const dates = src.join(', ');
      return `Monthly on day${src.length > 1 ? 's' : ''} ${dates || '(no days set)'} at ${timeStr}.`;
    }

    case 'yearly': {
      const src = (Array.isArray(n.months) && n.months.length)
        ? n.months
        : (Array.isArray(n.rule?.months) && n.rule.months.length)
          ? n.rule.months
          : [];
      const months = src.map(m => new Date(0, m - 1).toLocaleString('default', { month: 'short' })).join(', ');
      return `Yearly in ${months || '(no months set)'} at ${timeStr}.`;
    }

    default: {
      const d = toDateFromApi(raw);
      return d
        ? d.toLocaleString(undefined, { timeZone: effectiveTz || undefined })
        : '—';
    }
  }
})()}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4 flex-shrink-0">
                          <button
                            onClick={() => startEditing(n)}
                            className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteNotification(n.id)}
                            className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Form - only show if not at limit OR actively editing */}
            {(!reachedLimit || editingId) && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6">
                {editingId && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm">
                    <span className="font-semibold text-yellow-800">✏️ Editing notification</span>
                    <button
                      onClick={clearForm}
                      className="ml-2 text-blue-600 hover:text-blue-800 underline"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <input
                      className="input-field w-full"
                      placeholder="Notification Title"
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                      Bank Account
                    </label>
                    <select
                      className="input-field"
                      value={selectedBank}
                      onChange={e => setSelectedBank(e.target.value)}
                    >
                      <option value="">-- Choose Bank Account --</option>
                      {bankAccounts.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.name} (****{a.mask})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <select
                      className="input-field"
                      value={selectedPhone}
                      onChange={e => setSelectedPhone(e.target.value)}
                    >
                      <option value="">-- Choose Phone Number --</option>
                      {phones.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.phone_name} ({p.phone_number})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                      Frequency
                    </label>
                    <select
                      className="input-field"
                      value={frequency}
                      onChange={e => setFrequency(e.target.value)}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                      Schedule
                    </label>
                    <div className="mt-2">
                      {renderScheduleSection(false)}
                    </div>
                  </div>

                  {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                      {error}
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={saveNotification}
                      className="btn-primary flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
                    >
                      <CheckCircle className="w-5 h-5" />
                      {editingId ? 'Update Notification' : 'Add Notification'}
                    </button>
                    {editingId && (
                      <button
                        onClick={clearForm}
                        className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors duration-200 min-h-[44px] sm:min-h-0"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Show message when limit reached and not editing */}
            {reachedLimit && !editingId && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-6 text-center">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <strong className="text-green-900 text-lg">✓ All notifications created</strong>
                <p className="text-sm text-green-700 mt-2">
                  You've created all {maxNotifications} notification{maxNotifications > 1 ? 's' : ''} available on your plan.
                  You can edit existing notifications above or upgrade your plan for more.
                </p>
              </div>
            )}

            {/* Next Button */}
            <button
              onClick={async () => {
                // Advance onboarding to 'complete' then navigate
                try {
                  await api.post('/api/user/onboarding', { step: 'complete' });
                } catch (err) {
                  console.error('Failed to advance onboarding:', err);
                }
                navigate('/dashboard');
              }}
              className="w-full mt-6 min-h-[44px] px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
            >
              Continue to Dashboard
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}



export default function CreateNotification() {
  return <CreateNotificationForm />;
}